import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useProductCodes } from '../context/ProductCodesContext';

/**
 * ItemCodeLabel — the ONE component for rendering a raw-material / ingredient name
 * with its admin-side item code.
 *
 *                       has item code                no item code
 *   owner/admin    →    "RM-042 · Turmeric Root"     "Turmeric Root"
 *   manager/staff  →    "RM-042"                     "Turmeric Root"
 *   client-portal  →    "Turmeric Root"              "Turmeric Root"
 *
 * Admins get both so they always know what "RM-042" actually is. Staff sees
 * only the code (secrecy) but still sees the name when no code exists to avoid
 * a blank label. Client-portal users never see item codes.
 *
 * Props:
 *   - name: raw-material / ingredient name (required)
 *   - fallback: what to show when name is empty (default "—")
 *   - inline: render as span vs div (default span)
 *   - className, codeClass, nameClass, separator overrides
 *   - showCodeOnly: force code-only even for admin (rare; useful for dense grids)
 */
export default function ItemCodeLabel({
  name,
  fallback = '—',
  inline = true,
  className = '',
  codeClass = 'font-mono font-semibold text-amber-400',
  nameClass = 'text-zinc-100',
  separator = ' · ',
  showCodeOnly = false,
}) {
  const { user } = useAuth();
  const { lookup } = useProductCodes();
  const role = user?.role || '';
  const isClient = role === 'client';
  const isAdmin = role === 'owner' || role === 'admin';

  if (!name) return <span className={className}>{fallback}</span>;

  const code = lookup(name);
  const Wrap = inline ? 'span' : 'div';

  // Client-portal users never see item codes.
  if (isClient || !code) {
    return <Wrap className={className} data-testid="item-code-label"><span className={nameClass}>{name}</span></Wrap>;
  }

  // Admin / Owner — both code + name (unless showCodeOnly).
  if (isAdmin && !showCodeOnly) {
    return (
      <Wrap className={className} data-testid="item-code-label">
        <span className={codeClass}>{code}</span>
        <span className="text-zinc-500">{separator}</span>
        <span className={nameClass}>{name}</span>
      </Wrap>
    );
  }

  // Manager / staff / other roles — code only (name hidden for secrecy).
  return (
    <Wrap className={className} data-testid="item-code-label" title={name}>
      <span className={codeClass}>{code}</span>
    </Wrap>
  );
}
